import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Plot from 'react-plotly.js';
import { API_ENDPOINTS, VOL_ARB_PAIRS } from '../../apiConfig';
import { generateComparisonCurves } from '../../utils/sviUtils';
import './VolArb.css';

function VolArb() {
  // Pair selection state
  const [selectedPair, setSelectedPair] = useState(VOL_ARB_PAIRS[0]);

  // Data state
  const [matchedExpiries, setMatchedExpiries] = useState([]);
  const [allCryptoExpiries, setAllCryptoExpiries] = useState([]);
  const [allEtfExpiries, setAllEtfExpiries] = useState([]);
  const [selectedExpiry, setSelectedExpiry] = useState(null);
  const [cryptoChainData, setCryptoChainData] = useState(null);
  const [etfChainData, setEtfChainData] = useState(null);
  const [spotPrices, setSpotPrices] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  // Track user's current zoom/pan state to preserve across updates
  const userZoomRef = useRef(null);

  // Fetch matched expiries when pair changes
  useEffect(() => {
    setSelectedExpiry(null);
    setCryptoChainData(null);
    setEtfChainData(null);
    userZoomRef.current = null; // Reset zoom on pair change

    fetch(API_ENDPOINTS.CRYPTO_MATCHED_EXPIRIES(selectedPair.crypto, selectedPair.etf))
      .then(res => res.json())
      .then(data => {
        const matched = data.matched_expiries || [];
        setMatchedExpiries(matched);
        setAllCryptoExpiries(data.all_crypto || []);
        setAllEtfExpiries(data.all_etf || []);
        if (matched.length > 0) {
          setSelectedExpiry(matched[0]);
        } else if (data.all_crypto?.length > 0) {
          setSelectedExpiry({ expiry: data.all_crypto[0], crypto_expiry: data.all_crypto[0], etf_expiry: null });
        }
      })
      .catch(err => console.error('Failed to fetch matched expiries:', err));
  }, [selectedPair]);

  // Reset zoom when expiry changes
  useEffect(() => {
    userZoomRef.current = null;
  }, [selectedExpiry]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    if (!selectedExpiry) return;

    setLoading(true);
    setError(null);

    try {
      const cryptoExpiry = selectedExpiry.crypto_expiry;
      const etfExpiry = selectedExpiry.etf_expiry;

      const [cryptoRes, etfRes, spotRes] = await Promise.all([
        cryptoExpiry ? fetch(`${API_ENDPOINTS.CRYPTO_OPTION_CHAIN}?symbol=${selectedPair.crypto}&expiry=${cryptoExpiry}`).catch(() => null) : null,
        etfExpiry ? fetch(`${API_ENDPOINTS.CRYPTO_OPTION_CHAIN}?symbol=${selectedPair.etf}&expiry=${etfExpiry}`).catch(() => null) : null,
        fetch(API_ENDPOINTS.CRYPTO_SPOT_PRICES(selectedPair.crypto, selectedPair.etf))
      ]);

      const [cryptoData, etfData, spots] = await Promise.all([
        cryptoRes?.ok ? cryptoRes.json() : null,
        etfRes?.ok ? etfRes.json() : null,
        spotRes.json()
      ]);

      setCryptoChainData(cryptoData);
      setEtfChainData(etfData);
      setSpotPrices(spots);
      setLastUpdate(new Date());

      if (!cryptoData && !etfData) {
        setError('No data available for selected expiry');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedExpiry, selectedPair]);

  // Polling
  useEffect(() => {
    fetchData();
    if (autoRefresh) {
      const interval = setInterval(fetchData, 5000);
      return () => clearInterval(interval);
    }
  }, [fetchData, autoRefresh]);

  // Calculate arb metrics
  const arbMetrics = useMemo(() => {
    if (!cryptoChainData && !etfChainData) return null;

    const cryptoAtmIV = cryptoChainData?.svi_params?.atm_iv || 0;
    const etfAtmIV = etfChainData?.svi_params?.atm_iv || 0;

    return {
      atmIvSpread: (cryptoAtmIV - etfAtmIV) * 100,
      cryptoAtmIV: cryptoAtmIV * 100,
      etfAtmIV: etfAtmIV * 100,
      cryptoRho: cryptoChainData?.svi_params?.rho,
      etfRho: etfChainData?.svi_params?.rho,
      cryptoForward: cryptoChainData?.forward_price,
      etfForward: etfChainData?.forward_price,
      conversionRatio: spotPrices?.conversion_ratio,
      perpPrice: spotPrices?.perpetual?.mid_price,
      etfPrice: spotPrices?.etf?.mid_price,
    };
  }, [cryptoChainData, etfChainData, spotPrices]);

  // Calculate moneyness bounds from actual strikes
  const strikeBounds = useMemo(() => {
    const moneynesses = [];
    const cryptoForward = cryptoChainData?.forward_price || 1;
    const ratio = spotPrices?.conversion_ratio || 1;

    if (cryptoChainData?.strikes) {
      cryptoChainData.strikes.forEach(strike => {
        moneynesses.push(strike / cryptoForward);
      });
    }

    if (etfChainData?.strikes) {
      etfChainData.strikes.forEach(strike => {
        const normalizedStrike = strike * ratio;
        moneynesses.push(normalizedStrike / cryptoForward);
      });
    }

    if (moneynesses.length === 0) return null;

    const min = Math.min(...moneynesses);
    const max = Math.max(...moneynesses);
    const margin = (max - min) * 0.05;

    return {
      min: parseFloat((min - margin).toFixed(4)),
      max: parseFloat((max + margin).toFixed(4)),
    };
  }, [cryptoChainData, etfChainData, spotPrices]);

  // Generate smooth SVI curves within strike bounds
  const sviCurveData = useMemo(() => {
    if (!cryptoChainData && !etfChainData) return [];
    if (!strikeBounds) return [];

    const ratio = spotPrices?.conversion_ratio || 1;
    return generateComparisonCurves(cryptoChainData, etfChainData, ratio, 150, strikeBounds.min, strikeBounds.max) || [];
  }, [cryptoChainData, etfChainData, spotPrices, strikeBounds]);

  // Raw IV scatter points
  const rawIVData = useMemo(() => {
    if (!cryptoChainData && !etfChainData) return { crypto: [], etf: [] };

    const cryptoForward = cryptoChainData?.forward_price || 1;
    const ratio = spotPrices?.conversion_ratio || 1;

    const crypto = [];
    const etf = [];

    if (cryptoChainData?.strikes) {
      cryptoChainData.strikes.forEach((strike, i) => {
        const moneyness = strike / cryptoForward;
        if (cryptoChainData.raw_iv[i]) {
          crypto.push({
            moneyness: parseFloat(moneyness.toFixed(4)),
            strike,
            rawIV: parseFloat((cryptoChainData.raw_iv[i] * 100).toFixed(2)),
          });
        }
      });
    }

    if (etfChainData?.strikes) {
      etfChainData.strikes.forEach((strike, i) => {
        const normalizedStrike = strike * ratio;
        const moneyness = normalizedStrike / cryptoForward;
        if (etfChainData.raw_iv[i]) {
          etf.push({
            moneyness: parseFloat(moneyness.toFixed(4)),
            strike,
            rawIV: parseFloat((etfChainData.raw_iv[i] * 100).toFixed(2)),
          });
        }
      });
    }

    return { crypto, etf };
  }, [cryptoChainData, etfChainData, spotPrices]);

  // Handle zoom/pan changes from user
  const handleRelayout = useCallback((e) => {
    // Capture user's zoom/pan state
    if (e['xaxis.range[0]'] !== undefined && e['xaxis.range[1]'] !== undefined) {
      userZoomRef.current = {
        xMin: e['xaxis.range[0]'],
        xMax: e['xaxis.range[1]'],
        yMin: e['yaxis.range[0]'],
        yMax: e['yaxis.range[1]'],
      };
    }
    // Handle autorange (reset zoom)
    if (e['xaxis.autorange'] || e['yaxis.autorange']) {
      userZoomRef.current = null;
    }
  }, []);

  // Plotly data traces
  const plotData = useMemo(() => {
    const traces = [];

    // Crypto fitted IV line
    if (sviCurveData.length > 0 && sviCurveData.some(d => d.cryptoFittedIV)) {
      traces.push({
        x: sviCurveData.map(d => d.moneyness),
        y: sviCurveData.map(d => d.cryptoFittedIV),
        type: 'scatter',
        mode: 'lines',
        name: `${selectedPair.crypto} Fitted IV`,
        line: { color: '#3b82f6', width: 2 },
        hovertemplate: 'Moneyness: %{x:.3f}<br>Fitted IV: %{y:.2f}%<extra></extra>',
      });
    }

    // ETF fitted IV line
    if (sviCurveData.length > 0 && sviCurveData.some(d => d.etfFittedIV)) {
      traces.push({
        x: sviCurveData.map(d => d.moneyness),
        y: sviCurveData.map(d => d.etfFittedIV),
        type: 'scatter',
        mode: 'lines',
        name: `${selectedPair.etf} Fitted IV`,
        line: { color: '#10b981', width: 2 },
        hovertemplate: 'Moneyness: %{x:.3f}<br>Fitted IV: %{y:.2f}%<extra></extra>',
      });
    }

    // Crypto raw IV scatter
    if (rawIVData.crypto.length > 0) {
      traces.push({
        x: rawIVData.crypto.map(d => d.moneyness),
        y: rawIVData.crypto.map(d => d.rawIV),
        text: rawIVData.crypto.map(d => `Strike: $${d.strike.toLocaleString()}`),
        type: 'scatter',
        mode: 'markers',
        name: `${selectedPair.crypto} Raw IV`,
        marker: { color: '#3b82f6', size: 8, symbol: 'circle' },
        hovertemplate: 'Moneyness: %{x:.3f}<br>Raw IV: %{y:.2f}%<br>%{text}<extra></extra>',
      });
    }

    // ETF raw IV scatter
    if (rawIVData.etf.length > 0) {
      traces.push({
        x: rawIVData.etf.map(d => d.moneyness),
        y: rawIVData.etf.map(d => d.rawIV),
        text: rawIVData.etf.map(d => `Strike: $${d.strike.toLocaleString()}`),
        type: 'scatter',
        mode: 'markers',
        name: `${selectedPair.etf} Raw IV`,
        marker: { color: '#10b981', size: 8, symbol: 'diamond' },
        hovertemplate: 'Moneyness: %{x:.3f}<br>Raw IV: %{y:.2f}%<br>%{text}<extra></extra>',
      });
    }

    return traces;
  }, [sviCurveData, rawIVData, selectedPair]);

  // Plotly layout - preserve user zoom or use default bounds
  const plotLayout = useMemo(() => {
    // Determine X range: user zoom > default strike bounds
    let xRange;
    if (userZoomRef.current) {
      xRange = [userZoomRef.current.xMin, userZoomRef.current.xMax];
    } else if (strikeBounds) {
      xRange = [strikeBounds.min, strikeBounds.max];
    } else {
      xRange = [0.7, 1.3]; // Default fallback
    }

    // Determine Y range
    let yRange;
    if (userZoomRef.current && userZoomRef.current.yMin !== undefined) {
      yRange = [userZoomRef.current.yMin, userZoomRef.current.yMax];
    }

    return {
      title: null,
      autosize: true,
      height: 480,
      margin: { t: 40, r: 40, b: 60, l: 70 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)',
      font: { color: '#a1a1aa' },
      // uirevision keeps UI state (zoom/pan) stable across data updates
      uirevision: `${selectedPair.id}-${selectedExpiry?.expiry}`,
      xaxis: {
        title: 'Moneyness (K/F)',
        range: xRange,
        gridcolor: '#27272a',
        tickformat: '.2f',
        showgrid: true,
        zeroline: false,
      },
      yaxis: {
        title: 'Implied Volatility (%)',
        range: yRange,
        gridcolor: '#27272a',
        ticksuffix: '%',
        showgrid: true,
        zeroline: false,
      },
      legend: {
        orientation: 'h',
        yanchor: 'bottom',
        y: 1.02,
        xanchor: 'center',
        x: 0.5,
        bgcolor: 'rgba(0,0,0,0)',
      },
      hovermode: 'closest',
      dragmode: 'zoom',
      shapes: [{
        type: 'line',
        x0: 1,
        x1: 1,
        y0: 0,
        y1: 1,
        yref: 'paper',
        line: { color: '#f59e0b', width: 1, dash: 'dash' },
      }],
      annotations: [{
        x: 1,
        y: 1,
        yref: 'paper',
        text: 'ATM',
        showarrow: false,
        font: { color: '#f59e0b', size: 11 },
        yshift: 10,
      }],
    };
  }, [strikeBounds, selectedPair, selectedExpiry]);

  const plotConfig = {
    displayModeBar: true,
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    displaylogo: false,
    responsive: true,
    scrollZoom: true,
  };

  // Determine available expiries to show
  const expiriesToShow = matchedExpiries.length > 0
    ? matchedExpiries
    : allCryptoExpiries.map(e => ({ expiry: e, crypto_expiry: e, etf_expiry: allEtfExpiries.includes(e) ? e : null }));

  return (
    <div className="vol-arb">
      <div className="vol-arb-header">
        <h2>Vol Arbitrage: {selectedPair.label}</h2>
        <div className="controls">
          <div className="control-group">
            <label>Pair</label>
            <select
              value={selectedPair.id}
              onChange={e => setSelectedPair(VOL_ARB_PAIRS.find(p => p.id === e.target.value))}
            >
              {VOL_ARB_PAIRS.map(pair => (
                <option key={pair.id} value={pair.id}>{pair.label}</option>
              ))}
            </select>
          </div>

          <div className="control-group">
            <label>Expiry</label>
            <select
              value={selectedExpiry?.expiry || ''}
              onChange={e => {
                const exp = expiriesToShow.find(x => x.expiry === e.target.value);
                if (exp) setSelectedExpiry(exp);
              }}
              disabled={expiriesToShow.length === 0}
            >
              {expiriesToShow.map(e => (
                <option key={e.expiry} value={e.expiry}>
                  {e.expiry} {matchedExpiries.find(m => m.expiry === e.expiry) ? '' : '(partial)'}
                </option>
              ))}
            </select>
          </div>

          <label className="auto-refresh">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>

          <button onClick={fetchData} disabled={loading} className="refresh-btn">
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <span>Error: {error}</span>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}

      {/* Arb Metrics Panel */}
      {arbMetrics && (
        <div className="arb-metrics-panel">
          <div className={`metric-card highlight ${arbMetrics.atmIvSpread > 0 ? 'positive' : arbMetrics.atmIvSpread < 0 ? 'negative' : ''}`}>
            <span className="metric-label">ATM IV Spread</span>
            <span className="metric-value">
              {arbMetrics.atmIvSpread > 0 ? '+' : ''}{arbMetrics.atmIvSpread.toFixed(2)}%
            </span>
            <span className="metric-sublabel">{selectedPair.crypto} - {selectedPair.etf}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{selectedPair.crypto} ATM IV</span>
            <span className="metric-value crypto">{arbMetrics.cryptoAtmIV.toFixed(2)}%</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{selectedPair.etf} ATM IV</span>
            <span className="metric-value etf">{arbMetrics.etfAtmIV.toFixed(2)}%</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{selectedPair.crypto} Skew</span>
            <span className="metric-value">{arbMetrics.cryptoRho?.toFixed(4) || 'N/A'}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{selectedPair.etf} Skew</span>
            <span className="metric-value">{arbMetrics.etfRho?.toFixed(4) || 'N/A'}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">Price Ratio</span>
            <span className="metric-value">{arbMetrics.conversionRatio?.toFixed(2) || 'N/A'}x</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{selectedPair.crypto} Perp</span>
            <span className="metric-value">${arbMetrics.perpPrice?.toLocaleString(undefined, { maximumFractionDigits: 2 }) || 'N/A'}</span>
          </div>
          <div className="metric-card">
            <span className="metric-label">{selectedPair.etf} Price</span>
            <span className="metric-value">${arbMetrics.etfPrice?.toFixed(2) || 'N/A'}</span>
          </div>
          {lastUpdate && (
            <div className="metric-card">
              <span className="metric-label">Updated</span>
              <span className="metric-value time">{lastUpdate.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      )}

      {/* Chart */}
      <div className="chart-container">
        {loading && !plotData.length ? (
          <div className="loading">Loading vol curve data...</div>
        ) : plotData.length > 0 ? (
          <Plot
            data={plotData}
            layout={plotLayout}
            config={plotConfig}
            style={{ width: '100%', height: '480px' }}
            useResizeHandler={true}
            onRelayout={handleRelayout}
          />
        ) : (
          <div className="no-data">
            {selectedExpiry
              ? 'No data available for selected expiry'
              : 'Select an expiry to view the vol comparison'}
          </div>
        )}
      </div>

      {/* Legend explanation */}
      <div className="legend-explanation">
        <div className="legend-item">
          <span className="legend-color crypto"></span>
          <span>{selectedPair.crypto} (Deribit) - Crypto perpetual options</span>
        </div>
        <div className="legend-item">
          <span className="legend-color etf"></span>
          <span>{selectedPair.etf} (IBKR/CBOE) - ETF options</span>
        </div>
      </div>
    </div>
  );
}

export default VolArb;
